import { useCallback, useMemo, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { switchcase } from '@sweetgreen/garnish';

import type { useCustomer } from '@order/Customer';
import type { useCart } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import type { TelemetryEvent } from '@order/Telemetry';
import { useTelemetry } from '@order/Telemetry';

import { useOrderStatusTelemetry } from '../../../OrderStatusScreen/OrderStatus.telemetry';
import { getOrderTip } from '../../Checkout.utils';
import { useCheckoutContext } from '../../CheckoutProvider';
import type { useCheckoutPayload } from '../useCheckoutPayload';

export const useCheckoutTelemetry = ({
  customer,
  cart,
  cartOrderType: orderType,
  total,
  payload,
}: CheckoutTelemetryProps) => {
  // Cart
  const orderId = cart?.id;
  const restaurantId = cart?.restaurant?.id;
  const restaurantName = cart?.restaurant?.name;

  const orderTip = payload ? getOrderTip(payload, orderType) : '0';

  // Line Items
  const productIds = useMemo(
    () =>
      cart?.lineItems.map(({ product }) => product?.id ?? '')?.filter(Boolean),
    [cart?.lineItems],
  );
  const productNames = useMemo(
    () =>
      cart?.lineItems
        .map(({ product }) => product?.name ?? '')
        ?.filter(Boolean),
    [cart?.lineItems],
  );
  const bagCount = useMemo(
    () =>
      cart?.lineItems
        .map(({ quantity }) => quantity ?? 0)
        .reduce((count: number, quantity: number) => count + quantity, 0),
    [cart?.lineItems],
  );

  // Restaurant Address
  const restaurantAddress = useMemo(
    () => ({
      street: cart?.restaurant?.address ?? '',
      city: cart?.restaurant?.city ?? '',
      state: cart?.restaurant?.state ?? '',
      zipCode: cart?.restaurant?.zipCode ?? '',
      country: 'US',
    }),
    [
      cart?.restaurant?.address,
      cart?.restaurant?.city,
      cart?.restaurant?.state,
      cart?.restaurant?.zipCode,
    ],
  );

  // Delivery Order Detail
  const deliveryOrderDetail = useMemo(
    () => ({
      street: cart?.deliveryOrderDetail?.address?.street ?? '',
      city: cart?.deliveryOrderDetail?.address?.city ?? '',
      state: cart?.deliveryOrderDetail?.address?.state ?? '',
      zipCode: cart?.deliveryOrderDetail?.address?.zipCode ?? '',
      country: cart?.deliveryOrderDetail?.address?.country ?? 'US',
    }),
    [
      cart?.deliveryOrderDetail?.address?.city,
      cart?.deliveryOrderDetail?.address?.country,
      cart?.deliveryOrderDetail?.address?.state,
      cart?.deliveryOrderDetail?.address?.street,
      cart?.deliveryOrderDetail?.address?.zipCode,
    ],
  );

  // Customer
  const customerTelemetry = useMemo(() => {
    const addresses = getAddressesNames(customer);

    return {
      userId: customer?.id ?? '',
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
      addresses,
      hasDuplicateNameAddresses: hasDuplicateNameAddresses(addresses),
    };
  }, [customer]);

  const data: TelemetryEvent<'checkout.view'>['payload'] = useMemo(
    () => ({
      orderId,
      restaurantId,
      restaurantName,
      orderType,
      productIds,
      productNames,
      bagCount,
      orderValue: Number(((total ?? 0) / 100).toFixed(2)),
      orderTotal: Number(((total ?? 0) / 100).toFixed(2)),
      orderTip,
      customer: customerTelemetry,
      isDelivery: orderType === 'delivery',
      ...(orderType === 'delivery' ? deliveryOrderDetail : restaurantAddress),
    }),
    [
      orderId,
      restaurantId,
      restaurantName,
      orderType,
      productIds,
      productNames,
      bagCount,
      total,
      orderTip,
      customerTelemetry,
      deliveryOrderDetail,
      restaurantAddress,
    ],
  );

  const checkoutViewEventRef = useRef<TelemetryEvent<'checkout.view'>>();
  const checkoutViewEvent: TelemetryEvent<'checkout.view'> = useMemo(
    () => ({
      name: 'checkout.view',
      payload: data,
    }),
    [data],
  );

  // eslint-disable-next-line functional/immutable-data
  checkoutViewEventRef.current = checkoutViewEvent;

  const checkoutStartEvent: TelemetryEvent<'checkout.start'> = useMemo(
    () => ({
      name: 'checkout.start',
      payload: data,
    }),
    [data],
  );

  const checkoutSuccessEvent: TelemetryEvent<'checkout.success'> = useMemo(
    () => ({
      name: 'checkout.success',
      payload: data,
    }),
    [data],
  );

  return {
    data,
    checkoutViewEvent: checkoutViewEventRef,
    checkoutStartEvent,
    checkoutSuccessEvent,
  };
};

export const useCheckoutTelemetryEvents = (
  checkoutTelemetryProps: Parameters<typeof useCheckoutTelemetry>[0],
) => {
  const { track, trackEvent } = useTelemetry();
  const { t } = useLocalizationContext();
  const { handleOrderStatusEvent } = useOrderStatusTelemetry();
  const { checkoutStartEvent, checkoutSuccessEvent } = useCheckoutTelemetry(
    checkoutTelemetryProps,
  );

  const checkoutStart = useCallback(() => {
    trackEvent(checkoutStartEvent);
  }, [checkoutStartEvent, trackEvent]);

  const checkoutSuccess = useCallback(() => {
    handleOrderStatusEvent('checkout.success');
    trackEvent(checkoutSuccessEvent);
  }, [checkoutSuccessEvent, handleOrderStatusEvent, trackEvent]);

  const timeslotUnavailable = useCallback(
    (systemError: string) => {
      track('checkout.failure', {
        userError: t('checkout.timeslot-unavailable'),
        systemError,
      });
    },
    [t, track],
  );

  const deliveryMinimumNotMet = useCallback(
    (systemError: string, minimumDeliveryAmount: string) => {
      track('checkout.failure', {
        userError: t('bag.delivery-minimum', {
          minimum: minimumDeliveryAmount,
        }),
        systemError,
      });
    },
    [t, track],
  );

  const productUnavailable = useCallback(
    (systemError: string, errorMessage: string) => {
      track('checkout.failure', {
        userError: errorMessage,
        systemError,
      });
    },
    [track],
  );

  const restaurantIsNotAcceptingOrders = useCallback(
    (systemError: string, errorMessage: string) => {
      track('checkout.failure', {
        userError: errorMessage,
        systemError,
      });
    },
    [track],
  );

  const invalidTip = useCallback(
    (errorMessage: string) => {
      track('checkout.failure', { userError: errorMessage });
    },
    [track],
  );

  const otherKnownErrors = useCallback(
    (systemError: string, typename: string) => {
      track('checkout.failure', {
        userError: switchcase({
          InvalidBillingAccount: t('checkout.billing-account.invalid'),
          InvalidCustomerPhone: t('checkout.customer-phone.invalid'),
          InvalidTip: t('checkout.tip.invalid'),
          DeliveryAlreadyInFlight: t('checkout.delivery-in-flight'),
          DeliverySpecifyAddress: t('checkout.delivery-specify-address'),
        })(t('general.error'))(typename),
        systemError,
      });
    },
    [t, track],
  );

  const unknownCheckoutError = useCallback(
    (systemError: string) => {
      track('checkout.failure', { userError: t('general.error'), systemError });
    },
    [t, track],
  );

  return useMemo(
    () => ({
      checkoutStart,
      checkoutSuccess,
      timeslotUnavailable,
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      invalidTip,
      otherKnownErrors,
      unknownCheckoutError,
    }),
    [
      checkoutStart,
      checkoutSuccess,
      timeslotUnavailable,
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      invalidTip,
      otherKnownErrors,
      unknownCheckoutError,
    ],
  );
};

export const useCheckoutViewEventEffect = (
  props: CheckoutViewEventEffectProps & CheckoutTelemetryProps,
) => {
  const {
    customer,
    cart,
    cartOrderType,
    total,
    isCustomerDataReady,
    isLoadingCheckout,
  } = props;

  const { trackEvent } = useTelemetry();
  const { payload } = useCheckoutContext();

  const { checkoutViewEvent } = useCheckoutTelemetry({
    customer,
    cart,
    cartOrderType,
    total,
    payload,
  });

  const isCheckoutReady = isCustomerDataReady && !isLoadingCheckout;

  const trackCheckoutView = useCallback(() => {
    if (isCheckoutReady && checkoutViewEvent.current) {
      trackEvent(checkoutViewEvent.current);
    }
  }, [checkoutViewEvent, isCheckoutReady, trackEvent]);

  useFocusEffect(trackCheckoutView);
};

export const useConnectedCheckoutViewEventEffect = (
  props: CheckoutViewEventEffectProps,
) => {
  const { isCustomerDataReady, isLoadingCheckout } = props;
  const { customer, cart, total, cartOrderType, payload } =
    useCheckoutContext();

  useCheckoutViewEventEffect({
    customer,
    cart,
    cartOrderType,
    total,
    payload,
    isCustomerDataReady,
    isLoadingCheckout,
  });
};

// ─── Helpers ───────────────────────────────────────────────────────────

function getAddressesNames(
  customer: ReturnType<typeof useCustomer>['customer'],
) {
  return (customer?.addresses?.map(({ name }) => name)?.filter(Boolean) ??
    []) as readonly string[];
}

function hasDuplicateNameAddresses(addresses: readonly string[]) {
  const uniqueAddresses = new Set(addresses);

  return uniqueAddresses.size !== addresses.length;
}

// ─── Types ─────────────────────────────────────────────────────────────

type CheckoutViewEventEffectProps = {
  readonly isCustomerDataReady: boolean;
  readonly isLoadingCheckout: boolean;
};

type CheckoutTelemetryProps = {
  readonly customer: ReturnType<typeof useCustomer>['customer'];
  readonly cart: ReturnType<typeof useCart>['cart'];
  readonly cartOrderType: ReturnType<typeof useCart>['cartOrderType'];
  readonly total: ReturnType<typeof useCart>['total'];
  readonly payload?: ReturnType<typeof useCheckoutPayload>['payload'];
};
